import 'react-phone-number-input/style.css'
import {Input as CustomInput} from '../../../components/core/input'
import {isValidPhoneNumber} from 'react-phone-number-input'
import Input from 'react-phone-number-input/input'
import {SimpleBlueButton} from '../../../components/core/button'
import ButtonLoader from '../../../components/core/button/ButtonLoader'
import {Formik} from 'formik'
import {useEffect, useMemo, useState} from 'react'
import {channelTypeSchema} from '../../../utils/validation'
import {addChannel, setAddedChannel} from '../../../stores/customer-settings'
import {useDispatch, useSelector} from 'react-redux'
import channelTypes from '../../../data/channelTypes.json'
import LimitManager from '../../../utils/limitManager'
import CustomSelect from '../../../components/core/customSelect'
import {PhoneIcon, SmsIcon} from '../../../icons'
import classNames from 'classnames'
import localStorageKeys from '../../../utils/localStorageKeys'
import RenderedForPaid from '../../../components/pages/RenderedForPaid'
import plans from '../../../data/plans.json'
import {useNamespaceTranslations} from '../../../utils/hooks/useNamespacesTranslations'

const buildModifiedChannelTypes = (usageLimits, text) => {
    const phoneCallLimits = LimitManager.getPhoneCallChannelLimit(usageLimits)

    return {
        ...channelTypes.types,
        sms: {
            value: channelTypes.types.sms,
            isLimitDisabled: !LimitManager.getSMSChannelIsEnabled(usageLimits),
            limit: LimitManager.getSMSChannelLimit(usageLimits),
            limitKey: usageLimits.limit_keys.sms_count_per_month.limit_key,
            infoText: text.sms_type_info
        },
        phone: {
            value: channelTypes.types.phone,
            isLimitDisabled: phoneCallLimits === 0,
            limit: phoneCallLimits,
            limitKey: usageLimits.limit_keys.phone_call_count_per_month.limit_key,
            infoText: text.phone_type_info
        }
    }
}
const AddNotificationChannel = ({closeModal, setModalData, getNotificationChannelCount, customerId, checkVerify}) => {
    const text = useNamespaceTranslations('notifications')
    const usageLimits = useNamespaceTranslations('usage-limits')
    const [buttonIsDisabled, setButtonIsDisabled] = useState(true)
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [phoneNumber, setPhoneNumber] = useState('')
    const currentPlan = localStorage.getItem(localStorageKeys.PLAN)
    const [active, setActive] = useState(null)
    const addedChannel = useSelector(state => state.customer_settings.addedChannel)
    const isDarkMode = useSelector(state => state.sidebar.isDarkMode)
    const modifiedChannelTypes = useMemo(() => buildModifiedChannelTypes(usageLimits, text), [usageLimits, text])
    const isSMSChannelDisabled = modifiedChannelTypes.sms.isLimitDisabled

    const dispatch = useDispatch()

    const handleInfo = (index, state) => {
        state ? setActive(index) : setActive(null)
    }

    const channelTypeElement = (value, label, isDisabled, icon) => {
        return {value, label, isDisabled, icon}
    }

    const channelTypeOptions = [
        channelTypeElement(modifiedChannelTypes.sms.value, 'Phone number (SMS)', modifiedChannelTypes.sms.isLimitDisabled, <SmsIcon/>),
        channelTypeElement(modifiedChannelTypes.phone.value, 'Phone Call', modifiedChannelTypes.phone.isLimitDisabled, <PhoneIcon/>)
    ].filter(Boolean)

    const initialFormValues = {
        type: !isSMSChannelDisabled ? channelTypeOptions[0] : '',
        value: ''
    }

    const fieldNames = {
        channel_type: 'type',
        channel_value: 'value'
    }

    const validateNumber = () => {
        if (!isValidPhoneNumber(phoneNumber)) {
            setButtonIsDisabled(true)
            return text.number_is_not_valid
        }
        return setButtonIsDisabled(false)
    }

    const addNotificationChannel = async(values, {setFieldError, resetForm}) => {
        setIsSubmitting(true)
        const success = await dispatch(addChannel(false, customerId, {
            type: values.type.value,
            value: phoneNumber
        }, setFieldError, resetForm))
        setIsSubmitting(false)
        if (success) {
            closePopup()
        }
    }

    const getPaidText = (values) => {
        const channelValue = values.type.value
        const channelLimit = modifiedChannelTypes?.[channelValue]?.limit
        const channelType = values.type.label
        const channelCount = getNotificationChannelCount(channelValue)
        return  LimitManager.getLimitDiff(modifiedChannelTypes?.[channelValue]?.limitKey, channelCount) > 0 && text.for_paid_channel
            .replace(':channel_limit', channelLimit)
            .replace(':channel_type', channelType)
            .replace(':channel_count', LimitManager.getLimitDiff(modifiedChannelTypes?.[channelValue]?.limitKey, channelCount))
    }

    const isExceeded = (type = 'sms') => {
        return modifiedChannelTypes?.[type]?.limit === getNotificationChannelCount(type)
    }

    const closePopup = () => {
        setPhoneNumber('')
        closeModal()
    }
    useEffect(() => {
        if (addedChannel?.priority) {
            setModalData(prev => ({
                ...prev,
                status: true,
                codeModal: addedChannel?.new_value_verified === false,
                value: phoneNumber,
                type: addedChannel?.type,
                channel: addedChannel
            }))
            dispatch(setAddedChannel({}))
        }

        if (addedChannel?.new_value_verified === null) {
            checkVerify(addedChannel?.new_value)
            setModalData({})
        }
    }, [addedChannel])

    return (
        <Formik initialValues={initialFormValues}
            enableReinitialize={true}
            onSubmit={addNotificationChannel}
            validationSchema={channelTypeSchema}
            validateOnBlur={false}
            validateOnChange={false}>
            {({errors, handleSubmit, setFieldValue, setErrors, values}) => (
                <form onSubmit={handleSubmit} className="modal-body">
                    {values.type.value &&
                    <RenderedForPaid
                        forPaidText={getPaidText(values) || text.for_paid_general}
                        isExceeded={isExceeded(values.type.value)}
                        isTextWithLimits={!!getPaidText(values)}/>}
                    <CustomSelect
                        placeholder={text.channel_type}
                        onChange={setFieldValue}
                        infoText={text.type_info}
                        value={values.type}
                        name={fieldNames.channel_type}
                        options={channelTypeOptions}
                        activeInfo={active === fieldNames.channel_type}
                        handleInfo={(state) => handleInfo(fieldNames.channel_type, state)}
                        error={errors.type}
                    />
                    {values.type?.value && Object.values(modifiedChannelTypes?.[values.type.value])?.length > 0 && (
                        <CustomInput
                            customInput={
                                <Input
                                    className={classNames('simple-input', {
                                        dark: isDarkMode
                                    })}
                                    disabled={isExceeded(values.type.value)}
                                    placeholder={text.phone_number}
                                    name={fieldNames.channel_value}
                                    value={phoneNumber}
                                    onChange={e => {
                                        setPhoneNumber(e)
                                        setErrors({})
                                    }}
                                />
                            }
                            infoText={modifiedChannelTypes?.[values.type.value].infoText}
                            activeInfo={active === fieldNames.channel_value}
                            handleInfo={(state) => handleInfo(fieldNames.channel_value, state)}
                            error={(phoneNumber?.length > 0 && validateNumber()) || errors.value}
                        />
                    )}
                    <SimpleBlueButton
                        type="submit"
                        disabled={buttonIsDisabled || isSubmitting || isExceeded(values.type.value)}>
                        {isSubmitting && <ButtonLoader/>}
                        {text.submit}
                    </SimpleBlueButton>
                </form>
            )}
        </Formik>
    )
}

export default AddNotificationChannel
